/**
 * Resident Detail Content
 *
 * The resident detail body — everything except the modal chrome. Owns the
 * per-resident queries, the lookback windows, and the scrollable section stack:
 * - AI Summary (facility-gated)
 * - Progress Notes
 * - Vital Signs
 * - Medications
 * - Infections
 * - Fall Incidents
 * - Care Queue Notes
 * - Functional Abilities
 *
 * Two hosts render this:
 * - {@link ResidentDetailDialog} — the Care Queue modal, which adds the
 *   backdrop, ESC handling, and Close footer and passes `variant="modal"`.
 * - The embeddable resident-detail widget (`src/widget/resident-detail`), which
 *   mounts it inside My Navigator's resident tab with `variant="embedded"`.
 *
 * `variant` controls chrome only — rounding, shadow, and whether the header's
 * close affordance appears. It must NEVER gate which sections render: both
 * surfaces are required to show the same clinical detail.
 */

import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { AiSummarySection } from "./dialog-sections/AiSummarySection";
import { CareQueueNotesSection } from "./dialog-sections/CareQueueNotesSection";
import { ProgressNotesSection } from "./dialog-sections/ProgressNotesSection";
import { VitalSignsTrendsSection } from "./dialog-sections/VitalSignsTrendsSection";
import { MedicationsSection } from "./dialog-sections/MedicationsSection";
import { InfectionsSection } from "./dialog-sections/InfectionsSection";
import { FallIncidentsSection } from "./dialog-sections/FallIncidentsSection";
import { FunctionalAbilitiesSection } from "./dialog-sections/FunctionalAbilitiesSection";
import { X } from "lucide-react";
import { RiskLevelBadge } from "../molecules/RiskLevelBadge";
import { ResidentSummary, calculateAge } from "../../types/residents";
import type { AiFeedbackItem, AiFeedbackSentiment } from "../../types/residents";
import { useApiService } from "../../contexts/ApiServiceContext";
import { trackCareQueueEvent } from "../../lib/tracking";
import { cn } from "../ui/utils";

export type ResidentDetailVariant = "modal" | "embedded";

export interface ResidentDetailContentProps {
  resident: ResidentSummary;
  /** Chrome only — never gates which sections render. */
  variant?: ResidentDetailVariant;
  initialInsightsDays?: number;
  /**
   * Facility-disabled risk factors. Part of the contract (both hosts resolve
   * and pass it) but not yet consumed here — the sections currently rendered
   * don't surface per-factor rows. Kept so hosts don't have to change when
   * PrimaryRiskFactorsSection is enabled.
   */
  disabledRiskFactors?: string[];
  aiSummaryEnabled?: boolean;
  /** UI-visibility-only switch — reasoning is still generated/stored when false. */
  aiReasoningVisible?: boolean;
  /** When supplied, the header renders a close affordance that calls this. */
  onClose?: () => void;
}

export const ResidentDetailContent: React.FC<ResidentDetailContentProps> = ({
  resident,
  variant = "modal",
  initialInsightsDays,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- see prop docs
  disabledRiskFactors: _disabledRiskFactors = [],
  aiSummaryEnabled = false,
  aiReasoningVisible = false,
  onClose,
}) => {
  const api = useApiService();
  // Prefer wq_id (SHA-256, globally unique across facilities) as the path param.
  // Falls back to resident_id for backward compatibility.
  const residentId = resident.wq_id ?? resident.resident_id;
  const wqId = resident.wq_id;
  const isEnabled = !!residentId;
  const [insightsDays, setInsightsDays] = React.useState(initialInsightsDays ?? 3);

  // Sync insightsDays with the host's lookback when it changes.
  React.useEffect(() => {
    if (initialInsightsDays !== undefined) {
      setInsightsDays(initialInsightsDays);
    }
  }, [initialInsightsDays]);

  // Analytics: emit when the detail is opened. The hosts only mount this
  // component when the detail is actually shown, so mount == opened.
  React.useEffect(() => {
    trackCareQueueEvent("care_queue_resident_detail_opened", {
      residentId: String(resident.wq_id || resident.resident_id),
    });
  }, [resident.wq_id, resident.resident_id]);

  const vitalSignsWindow = 10; // Fixed window for vital signs trend
  // Default the Functional Abilities trend window to whatever the user picked
  // on the grid; still user-overridable via the in-section dropdown
  // (NETSOL-11382).
  const [functionalAbilitiesDays, setFunctionalAbilitiesDays] =
    React.useState(initialInsightsDays ?? 3);

  // Re-sync with the host's lookback when it changes, so each new drill-down
  // starts from the current grid selection.
  React.useEffect(() => {
    if (initialInsightsDays !== undefined) {
      setFunctionalAbilitiesDays(initialInsightsDays);
    }
  }, [initialInsightsDays]);

  // 1. Care Queue Notes
  const careQueueNotesQuery = useQuery({
    queryKey: ["resident-care-queue-notes", residentId],
    queryFn: () => api.getResidentCareQueueNotes(residentId!, undefined, undefined, wqId),
    enabled: isEnabled,
    staleTime: 60 * 1000,
  });

  // 2. Medications
  const medicationsQuery = useQuery({
    queryKey: ["resident-medications", residentId, insightsDays],
    queryFn: () => api.getResidentMedications(residentId!, undefined, insightsDays, undefined, undefined, wqId),
    enabled: isEnabled,
    staleTime: 60 * 1000,
  });

  // 3. Progress Notes
  const progressNotesQuery = useQuery({
    queryKey: ["resident-progress-notes", residentId, insightsDays],
    queryFn: () => api.getResidentProgressNotes(residentId!, undefined, insightsDays, undefined, wqId),
    enabled: isEnabled,
    staleTime: 60 * 1000,
  });

  // 4. Vital Signs
  const vitalSignsQuery = useQuery({
    queryKey: ["resident-vital-signs", residentId, vitalSignsWindow],
    queryFn: () => api.getResidentVitalSigns(residentId!, undefined, vitalSignsWindow, undefined, wqId),
    enabled: isEnabled,
    staleTime: 60 * 1000,
  });

  // 5. Infections (fetch all active, not limited to insights window)
  const infectionsQuery = useQuery({
    queryKey: ["resident-infections", residentId],
    queryFn: () => api.getResidentInfections(residentId!, undefined, undefined, undefined, wqId),
    enabled: isEnabled,
    staleTime: 60 * 1000,
  });

  // 6. Fall Incidents
  const fallIncidentsQuery = useQuery({
    queryKey: ["resident-fall-incidents", residentId, insightsDays],
    queryFn: () => api.getResidentFallIncidents(residentId!, insightsDays, undefined, wqId),
    enabled: isEnabled,
    staleTime: 60 * 1000,
  });

  // 7. Risk Factor Summary
  const riskFactorsQuery = useQuery({
    queryKey: ["resident-risk-factors", residentId],
    queryFn: () => api.getResidentRiskFactors(residentId!, undefined, undefined, wqId),
    enabled: isEnabled,
    staleTime: 60 * 1000,
  });

  // 8. Functional Abilities (Section GG N-Day Trend, window selectable in UI)
  const functionalAbilitiesQuery = useQuery({
    queryKey: [
      "resident-functional-abilities",
      residentId,
      functionalAbilitiesDays,
    ],
    queryFn: () =>
      api.getResidentFunctionalAbilities(
        residentId!,
        undefined,
        wqId,
        functionalAbilitiesDays
      ),
    enabled: isEnabled,
    staleTime: 60 * 1000,
  });

  // 9. AI Feedback (pre-populate thumbs state)
  const riskDocumentId = resident.wq_id ?? "";
  const feedbackQuery = useQuery({
    queryKey: ["ai-feedback", residentId],
    queryFn: ({ signal }) =>
      api.getAiFeedback(
        residentId!,
        riskDocumentId,
        undefined,
        wqId,
        signal
      ),
    enabled: isEnabled,
    staleTime: 30 * 1000,
  });

  const getExistingFeedback = (
    contentType: string,
    sourceItemId?: string
  ): { sentiment: AiFeedbackSentiment; comment?: string | null } | null => {
    const items: AiFeedbackItem[] = feedbackQuery.data?.feedback ?? [];
    const match = items.find(
      (f) =>
        f.content_type === contentType &&
        (sourceItemId ? f.source_item_id === sourceItemId : !f.source_item_id)
    );
    return match ? { sentiment: match.sentiment, comment: match.comment } : null;
  };

  const resolvedAiSummary = useMemo(() => {
    const periodSummary =
      riskFactorsQuery.data?.ai_summary_by_period?.[String(insightsDays)];
    // Only fall back to top-level ai_summary for old data that lacks per-period entries.
    // When the period key exists (even as "skipped"), respect it — never substitute
    // a different period's summary.
    if (periodSummary) {
      return {
        summary: periodSummary.summary ?? null,
        status: periodSummary.status,
        reasoning: periodSummary.reasoning ?? null,
      };
    }
    return {
      summary: riskFactorsQuery.data?.ai_summary ?? null,
      status: riskFactorsQuery.data?.ai_summary_status,
      reasoning: riskFactorsQuery.data?.ai_summary_reasoning ?? null,
    };
  }, [riskFactorsQuery.data, insightsDays]);

  // Resolve the 30-day summary separately for the "Additional Insights" section
  const resolved30DaySummary = useMemo(() => {
    if (insightsDays === 30) return { summary: null, status: undefined };
    const period30 = riskFactorsQuery.data?.ai_summary_by_period?.["30"];
    if (period30) {
      return {
        summary: period30.summary ?? null,
        status: period30.status,
      };
    }
    return {
      summary: riskFactorsQuery.data?.ai_summary ?? null,
      status: riskFactorsQuery.data?.ai_summary_status,
    };
  }, [riskFactorsQuery.data, insightsDays]);

  // Build header info from the resident row
  const displayName = resident.resident_name ?? "—";
  const displaySubtitle = `HR #: ${resident.health_record_number ?? "—"} • Room: ${resident.room ?? "—"} • Age: ${calculateAge(resident.date_of_birth) ?? "—"}`;

  return (
    <div
      // Marks the printable region for the dialog's "Export to PDF" (NETSOL-12075).
      data-resident-pdf-root
      className={cn(
        // Fills its host: the modal's flex column (above the Close footer) or
        // the widget's shadow container. Both are flex columns with a height.
        "flex min-h-0 flex-1 flex-col overflow-hidden bg-white",
        variant === "embedded" && "h-full",
      )}
    >
      {/* Header - Teal background per Figma */}
      <div className="flex-shrink-0 px-4 py-2 bg-[#228189]">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-white">
                {displayName}
              </h2>
              <RiskLevelBadge level={resident.risk_level} className="items-start!" />

              <p className="text-xs text-white/80">
                {displaySubtitle}
              </p>
            </div>

          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="rounded-sm opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-white/50"
            >
              <X className="h-5 w-5 text-white" />
              <span className="sr-only">Close</span>
            </button>
          )}
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-3 min-h-0">
        <div className="space-y-4">
          {aiSummaryEnabled && (
            <AiSummarySection
              summary={resolvedAiSummary.summary}
              isLoading={riskFactorsQuery.isLoading}
              aiSummaryStatus={resolvedAiSummary.status}
              aiSummaryReasoning={resolvedAiSummary.reasoning}
              aiReasoningVisible={aiReasoningVisible}
              residentId={resident.resident_id}
              riskDocumentId={resident.wq_id ?? ""}
              wqId={resident.wq_id}
              insightsDays={insightsDays}
              existingFeedbackSentiment={getExistingFeedback("ai_summary")?.sentiment}
              existingFeedbackComment={getExistingFeedback("ai_summary")?.comment}
              additionalSummary={resolved30DaySummary.summary}
              additionalSummaryStatus={resolved30DaySummary.status}
            />
          )}
          <ProgressNotesSection
            data={progressNotesQuery.data ?? null}
            isLoading={progressNotesQuery.isLoading}
            error={progressNotesQuery.error instanceof Error ? progressNotesQuery.error : null}
            days={insightsDays}
            onDaysChange={setInsightsDays}
            residentId={resident.resident_id}
            riskDocumentId={resident.wq_id ?? ""}
            wqId={resident.wq_id}
            feedbackItems={feedbackQuery.data?.feedback ?? []}
            insightsDays={insightsDays}
            reasoningVisible={aiReasoningVisible}
          />
          <VitalSignsTrendsSection
            data={vitalSignsQuery.data ?? null}
            isLoading={vitalSignsQuery.isLoading}
            error={vitalSignsQuery.error instanceof Error ? vitalSignsQuery.error : null}
          />
          <MedicationsSection
            data={medicationsQuery.data ?? null}
            isLoading={medicationsQuery.isLoading}
            error={medicationsQuery.error instanceof Error ? medicationsQuery.error : null}
            newOnly={true}
            window={insightsDays}
          />
          <InfectionsSection
            data={infectionsQuery.data ?? null}
            isLoading={infectionsQuery.isLoading}
            error={infectionsQuery.error instanceof Error ? infectionsQuery.error : null}
            insightsDays={insightsDays}
          />
          <FallIncidentsSection
            data={fallIncidentsQuery.data ?? null}
            isLoading={fallIncidentsQuery.isLoading}
            error={fallIncidentsQuery.error instanceof Error ? fallIncidentsQuery.error : null}
          />
          <CareQueueNotesSection
            data={careQueueNotesQuery.data ?? null}
            isLoading={careQueueNotesQuery.isLoading}
            error={careQueueNotesQuery.error instanceof Error ? careQueueNotesQuery.error : null}
          />
          <FunctionalAbilitiesSection
            data={functionalAbilitiesQuery.data ?? null}
            isLoading={functionalAbilitiesQuery.isLoading}
            error={functionalAbilitiesQuery.error instanceof Error ? functionalAbilitiesQuery.error : null}
            days={functionalAbilitiesDays}
            onDaysChange={setFunctionalAbilitiesDays}
          />
        </div>
      </div>
    </div>
  );
};
